import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import AccordionPanel from '@/components/UI/AccordionPanel/AccordionPanel';
import HeaderWithTooltip from '@/components/UI/TableListEditable/HeaderWithTooltip/HeaderWithTooltip';
import TableListEditableNew from '@/components/UI/TableListEditable/TableListEditableNew';
import { VEHICLE_STATUS_COMMON_COUNT_COLUMNS, VEHICLE_STATUS_OVERVIEW_COLUMNS } from '@/helpers/constants';
import { parseObjectNegativeValuesToBlank } from '@/helpers/utils';
import useError from '@/hooks/useError';
import { getCampaignInfo } from '@/redux/Campaign/campaign.reducer';
import { getHeaderLanguage } from '@/redux/Header/header.reducer';
import { setAreaHelp } from '@/redux/Help/help.action';
import campaignServices from '@/services/campaignServices';
import type { CampaignInfo } from '@/types';
import type { ColumnConfig } from '@/types/TableListEditable';

type VehicleStatusOverviewProps = {
  headerLanguage?: string | null;
  campaignInfo: CampaignInfo;
  setHelpArea: (help: string) => void;
};

type VehicleStatusOverviewBlockingLocation = {
  location: string;
  validatedCars?: number | null;
  noRework?: number | null;
  reworkNotStarted?: number | null;
  reworkOpen?: number | null;
  reworkFinished?: number | null;
  okWithoutRework?: number | null;
  reworkInField?: number | null;
};

type VehicleStatusOverviewCampaign = {
  campaign: string;
  dynamic?: boolean;
  validatedCars?: number | null;
  noRework?: number | null;
  reworkNotStarted?: number | null;
  reworkOpen?: number | null;
  reworkFinished?: number | null;
  okWithoutRework?: number | null;
  reworkInField?: number | null;
  blockingLocations?: VehicleStatusOverviewBlockingLocation[];
};

type VehicleStatusOverviewSubCr = {
  subCrId: string;
  validatedCars?: number | null;
  noRework?: number | null;
  reworkNotStarted?: number | null;
  reworkOpen?: number | null;
  reworkFinished?: number | null;
  okWithoutRework?: number | null;
  reworkInField?: number | null;
  campaigns?: VehicleStatusOverviewCampaign[];
};

type VehicleStatusOverviewData = {
  crId: string;
  estimatedCars?: number | null;
  validatedCars?: number | null;
  noRework?: number | null;
  reworkNotStarted?: number | null;
  reworkOpen?: number | null;
  reworkFinished?: number | null;
  okWithoutRework?: number | null;
  reworkInField?: number | null;
  subCrs?: VehicleStatusOverviewSubCr[];
};

type VehicleStatusOverviewRow = Record<string, unknown> & { key: string };

const VehicleStatusOverview: React.FC<VehicleStatusOverviewProps> = ({ headerLanguage, campaignInfo, setHelpArea }) => {
  const { t } = useTranslation();
  const { handleCriticalError } = useError();
  const { id, version } = campaignInfo;

  const [vehiclesStatus, setVehiclesStatus] = useState<VehicleStatusOverviewData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    campaignServices.getVehiclesStatus(id, version)
      .then((res: VehicleStatusOverviewData) => {
        setVehiclesStatus(res);
        setLoading(false);
      })
      .catch((err) => {
        handleCriticalError(err);
        setLoading(false);
      });
  }, [id, version, handleCriticalError]);

  const columns = useMemo<ColumnConfig[]>(() => [
    { name: VEHICLE_STATUS_OVERVIEW_COLUMNS.MAIN_CR, title: t('plantFeedback.mainCr'), flex: 2 },
    { name: VEHICLE_STATUS_OVERVIEW_COLUMNS.SUB_CR, title: t('plantFeedback.subCr'), flex: 2 },
    { name: VEHICLE_STATUS_OVERVIEW_COLUMNS.CAMPAIGN, title: t('feedback.campaignNumber'), flex: 1.5 },
    { name: VEHICLE_STATUS_OVERVIEW_COLUMNS.DYNAMIC, title: t('plantFeedback.dynamic') },
    { name: VEHICLE_STATUS_OVERVIEW_COLUMNS.BLOCKING_LOCATION, title: t('plantFeedback.blockingLocation'), flex: 1.5 },
    {
      name: VEHICLE_STATUS_OVERVIEW_COLUMNS.ESTIMATED_CARS,
      title: t('plantFeedback.estimatedCars'),
      headerComponent: HeaderWithTooltip,
      headerComponentParams: { tooltipText: t('plantFeedback.estimatedCarsTooltip'), isWarningTooltip: false },
    },
    { name: VEHICLE_STATUS_OVERVIEW_COLUMNS.VALIDATED_CARS, title: t('plantFeedback.validatedCars') },
    { name: VEHICLE_STATUS_OVERVIEW_COLUMNS.NO_REWORK, title: t('plantFeedback.noRework') },
    {
      name: VEHICLE_STATUS_OVERVIEW_COLUMNS.REWORK_NOT_STARTED,
      title: t('plantFeedback.reworkNotStarted'),
      headerComponent: HeaderWithTooltip,
      headerComponentParams: { tooltipText: t('plantFeedback.reworkNotStartedTooltip'), isWarningTooltip: false },
    },
    { name: VEHICLE_STATUS_OVERVIEW_COLUMNS.REWORK_OPEN, title: t('plantFeedback.reworkOpen') },
    { name: VEHICLE_STATUS_OVERVIEW_COLUMNS.REWORK_FINISHED, title: t('plantFeedback.reworkFinished') },
    { name: VEHICLE_STATUS_OVERVIEW_COLUMNS.OK_WITHOUT_REWORK, title: t('feedback.withoutRework') },
    { name: VEHICLE_STATUS_OVERVIEW_COLUMNS.REWORK_IN_FIELD, title: t('plantFeedback.reworkInField') },
  ], [t]);

  const tableData = useMemo<VehicleStatusOverviewRow[]>(() => {
    if (!vehiclesStatus) return [];

    const rows: VehicleStatusOverviewRow[] = [];

    const MAIN_CR_FIELDS = [...VEHICLE_STATUS_COMMON_COUNT_COLUMNS, VEHICLE_STATUS_OVERVIEW_COLUMNS.ESTIMATED_CARS];
    rows.push({
      key: vehiclesStatus.crId,
      [VEHICLE_STATUS_OVERVIEW_COLUMNS.MAIN_CR]: vehiclesStatus.crId,
      ...parseObjectNegativeValuesToBlank(MAIN_CR_FIELDS, vehiclesStatus),
    });

    vehiclesStatus.subCrs?.forEach((subCr) => {
      rows.push({
        key: `${vehiclesStatus.crId}_${subCr.subCrId}`,
        [VEHICLE_STATUS_OVERVIEW_COLUMNS.SUB_CR]: subCr.subCrId,
        isSubCRChildren: true,
        ...parseObjectNegativeValuesToBlank(VEHICLE_STATUS_COMMON_COUNT_COLUMNS, subCr),
      });

      subCr.campaigns?.forEach((campaign) => {
        rows.push({
          key: `${vehiclesStatus.crId}_${subCr.subCrId}_${campaign.campaign}`,
          [VEHICLE_STATUS_OVERVIEW_COLUMNS.CAMPAIGN]: campaign.campaign,
          [VEHICLE_STATUS_OVERVIEW_COLUMNS.DYNAMIC]: campaign.dynamic
            ? t('plantFeedback.dynamicYes', { lng: headerLanguage?.toLowerCase() })
            : t('plantFeedback.dynamicNo', { lng: headerLanguage?.toLowerCase() }),
          thirdLevelRow: true,
          ...parseObjectNegativeValuesToBlank(VEHICLE_STATUS_COMMON_COUNT_COLUMNS, campaign),
        });

        campaign.blockingLocations?.forEach((location) => {
          rows.push({
            key: `${vehiclesStatus.crId}_${subCr.subCrId}_${campaign.campaign}_${location.location}`,
            [VEHICLE_STATUS_OVERVIEW_COLUMNS.BLOCKING_LOCATION]: location.location,
            fourthLevelRow: true,
            ...parseObjectNegativeValuesToBlank(VEHICLE_STATUS_COMMON_COUNT_COLUMNS, location),
          });
        });
      });
    });

    return rows;
  }, [vehiclesStatus, headerLanguage, t]);

  return (
    <div>
      <AccordionPanel
        title={t('plantFeedback.vehicleStatusOverview')}
        handleClick={() => setHelpArea('')}
      >
        <TableListEditableNew<VehicleStatusOverviewRow>
          list={tableData}
          columns={columns}
          columnId="key"
          loading={loading}
          isPaginated={false}
        />
      </AccordionPanel>
    </div>
  );
};

const mapStateToProps = (state: Parameters<typeof getHeaderLanguage>[0]) => ({
  headerLanguage: getHeaderLanguage(state),
  campaignInfo: getCampaignInfo(state),
});

const actionCreators = {
  setHelpArea: setAreaHelp,
};

export default connect(mapStateToProps, actionCreators)(VehicleStatusOverview);
